import { useEffect, useRef, useState } from 'react'
import { useWavesurfer } from '@wavesurfer/react'
import { Pause, Play } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

/** Peaks and duration of a sound already decoded by another player. */
export interface DecodedAudio {
  peaks: number[][]
  duration: number
}

interface Props {
  /**
   * Audio to load (token included). A function is called only when loading
   * starts, so a card that scrolls into view late gets a token still valid.
   * Undefined: placeholder bars only.
   */
  audioUrl?: string | (() => Promise<string | undefined>)
  /**
   * Shared audio element: every player of the same call drives one sound
   * (same playback, same position). Without it, the player owns its sound.
   */
  media?: HTMLMediaElement
  /** Already decoded by another player of the same `media`: no second download. */
  decoded?: DecodedAudio | null
  /** Duration known from the server (seconds), shown before the audio loads. */
  knownDuration?: number | null
  /** Loads the audio only once the player is on screen (long conversations). */
  lazy?: boolean
  /** Smaller, for a call card. */
  compact?: boolean
  /** Called once the audio is decoded, so other players can reuse it. */
  onDecoded?: (decoded: DecodedAudio) => void
}

const fmt = (s: number) => {
  if (!Number.isFinite(s) || s <= 0) return '0:00'
  return `${Math.floor(s / 60)}:${Math.floor(s % 60).toString().padStart(2, '0')}`
}

/**
 * Recording player with a waveform: play/pause, click or drag on the wave to
 * seek, elapsed time over total duration. Used by the call history and by
 * the call cards of a conversation.
 */
export function WavePlayer({ audioUrl, media, decoded, knownDuration, lazy = false, compact = false, onDecoded }: Props) {
  const { t } = useTranslation()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(!lazy)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (visible || !rootRef.current) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(rootRef.current)
    return () => observer.disconnect()
  }, [visible])

  // The source is fixed when loading starts: options that change afterwards
  // would rebuild the wave and download the audio again.
  const [source, setSource] = useState<{ url: string; decoded: DecodedAudio | null } | null>(null)
  useEffect(() => {
    if (!visible || source || !audioUrl) return
    const shared = media && (media.currentSrc || media.src)
    if (decoded && shared) {
      setSource({ url: shared, decoded })
      return
    }
    if (typeof audioUrl === 'string') {
      setSource({ url: audioUrl, decoded: null })
      return
    }
    let cancelled = false
    void audioUrl().then((url) => {
      if (!cancelled && url) setSource({ url, decoded: null })
    })
    return () => {
      cancelled = true
    }
  }, [visible, source, audioUrl, decoded, media])

  const height = compact ? 32 : 40
  const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
    container: containerRef,
    url: source?.url,
    // Not before the source is fixed: wavesurfer would load whatever the
    // shared element already holds, then be rebuilt mid-download.
    media: source ? media : undefined,
    peaks: source?.decoded?.peaks,
    duration: source?.decoded?.duration,
    waveColor: 'rgba(148,163,184,0.5)',
    progressColor: '#6366f1',
    cursorColor: '#4338ca',
    cursorWidth: compact ? 1 : 2,
    barWidth: compact ? 2 : 3,
    barGap: compact ? 1.5 : 2,
    barRadius: 3,
    height,
    normalize: true,
    interact: true,
    dragToSeek: true,
    autoplay: false,
    backend: 'MediaElement',
  })

  // Ready = the wave is drawn. With reused peaks wavesurfer is ready before
  // anyone listens, so the decoded data is checked as well as the event.
  const [isReady, setIsReady] = useState(false)
  useEffect(() => {
    if (!wavesurfer) {
      setIsReady(false)
      return
    }
    const pret = () => {
      setIsReady(true)
      // A shared sound paused mid-way: show where it stands, not 0:00.
      if (media && media.paused && media.currentTime > 0) wavesurfer.setTime(media.currentTime)
    }
    if (wavesurfer.getDecodedData()) pret()
    else setIsReady(false)
    const offs = [
      wavesurfer.on('error', (e) => {
        // A download cut short by a rebuild or a close is not a failure.
        if ((e as Error)?.name !== 'AbortError') setFailed(true)
      }),
      wavesurfer.on('ready', () => {
        setFailed(false)
        pret()
        if (!source?.decoded) onDecoded?.({ peaks: wavesurfer.exportPeaks(), duration: wavesurfer.getDuration() })
      }),
    ]
    return () => offs.forEach((off) => off())
    // onDecoded is read when the wave is ready, not a reason to resubscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wavesurfer, source])

  useEffect(() => {
    return () => {
      // A shared sound keeps playing for the call's other players.
      if (!media) wavesurfer?.pause()
    }
  }, [wavesurfer, media])

  const duration = isReady ? wavesurfer?.getDuration() ?? 0 : knownDuration ?? 0
  // A shared sound may already be playing when this player opens.
  const playing = media ? isReady && !media.paused : isPlaying

  return (
    <div ref={rootRef} className={cn('flex items-center flex-1 min-w-0', compact ? 'gap-2.5' : 'gap-3')}>
      <button
        type="button"
        onClick={() => wavesurfer?.playPause()}
        disabled={!isReady}
        aria-label={playing ? t('manager.history.player.pause') : t('manager.history.player.play')}
        title={playing ? t('manager.history.player.pause') : t('manager.history.player.play')}
        className={cn(
          'rounded-full flex items-center justify-center flex-shrink-0 transition-all disabled:opacity-40',
          compact ? 'w-7 h-7' : 'w-8 h-8',
          playing
            ? 'bg-indigo-500 text-white shadow-md shadow-indigo-500/30'
            : 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-200 dark:hover:bg-indigo-500/30',
        )}
      >
        {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
      </button>

      <div
        className={cn('flex-1 min-w-0 relative rounded-lg overflow-hidden', isReady && 'cursor-pointer')}
        style={{ height, background: 'rgba(99,102,241,0.06)' }}
      >
        {!isReady && (
          <div className="absolute inset-0 flex items-center gap-0.5 px-2">
            {Array.from({ length: 60 }).map((_, i) => (
              <div
                key={i}
                className={cn('flex-1 rounded-full', source && !failed && 'animate-pulse')}
                style={{
                  height: `${(compact ? 6 : 8) + Math.abs(Math.sin(i * 0.5)) * (height - 18)}px`,
                  background: 'rgba(99,102,241,0.2)',
                  animationDelay: `${i * 15}ms`,
                }}
              />
            ))}
          </div>
        )}
        <div ref={containerRef} style={{ display: isReady ? 'block' : 'none' }} />
      </div>

      <span
        className={cn(
          'tabular-nums flex-shrink-0 text-slate-400 dark:text-slate-500',
          compact ? 'text-[11px]' : 'text-xs font-mono',
        )}
      >
        {failed ? t('manager.history.player.unavailable') : `${fmt(media && isReady ? media.currentTime : currentTime)} / ${fmt(duration)}`}
      </span>
    </div>
  )
}
